import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { UserProfileMenu } from '@/components/UserProfileMenu';

interface AppHeaderProps {
  backTo?: string;
  backLabel?: string;
  onBack?: () => void;
  rightContent?: ReactNode;
  title?: string;
  subtitle?: string;
  action?: ReactNode;
  className?: string;
}

const AppHeader = ({
  backTo,
  backLabel,
  onBack,
  rightContent,
  title,
  subtitle,
  action,
  className,
}: AppHeaderProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    if (backTo) {
      navigate(backTo);
    }
  };

  const showBack = Boolean(onBack || backTo);

  return (
    <header className={cn('bg-card border-b border-border shadow-sm', className)}>
      <div className="container mx-auto px-4 py-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3">
            {showBack && (
              <Button
                variant="ghost"
                onClick={handleBack}
                className="gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
                {backLabel || t('back')}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {rightContent}
            <LanguageSwitcher />
            <UserProfileMenu />
          </div>
        </div>
        {(title || action) && (
          <div
            className={cn(
              'mt-4 flex flex-wrap items-start gap-3',
              title ? 'justify-between' : 'justify-end'
            )}
          >
            {title && (
              <div>
                <h1 className="text-2xl font-bold">{title}</h1>
                {subtitle && <p className="text-muted-foreground">{subtitle}</p>}
              </div>
            )}
            {action && <div className="flex items-center gap-2">{action}</div>}
          </div>
        )}
      </div>
    </header>
  );
};

export default AppHeader;
